import { firmBusinessCardColumns } from "@/components/features/settings/firm/firm-business-cards/firm-business-cards-columns";
import {
  Button,
  DataTable,
  ServerErrorFallbackCard,
  Typography,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { getFirmVirtualBusinessCardsQueryOptions } from "@/lib/data/react-query/firm/firm-business-card";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { type FirmVirtualBusinessCard } from "@/lib/types/Firm";
import { ErrorBoundary } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { PlusCircle } from "lucide-react";
import { useState } from "react";
import { AddOrEditFirmBusinessCardModal } from "./firm-business-cards/AddOrEditFirmBusinessCardModal";

function FirmBusinessCardsViewDangerous() {
  const { hasActionPermission } = useUser();
  const {
    data: firmBusinessCards,
    isLoading: isFirmBusinessCardsLoading,
    isError: isServerError,
  } = useQuery(getFirmVirtualBusinessCardsQueryOptions());

  const [isAddingBusinessCard, setIsAddingBusinessCard] = useState(false);
  const [editingBusinessCard, setEditingBusinessCard] = useState<
    FirmVirtualBusinessCard | undefined
  >();

  return (
    <div>
      {isServerError ? (
        <ServerErrorFallbackCard />
      ) : (
        <DataTable
          entityLabelPlural="business cards"
          columns={firmBusinessCardColumns}
          data={firmBusinessCards?.data ?? []}
          tableName={tanstackTableNames.firmBusinessCards}
          isLoading={isFirmBusinessCardsLoading}
          onRowClick={(row) => {
            setEditingBusinessCard(row.original);
          }}
          tableButtons={
            <>
              {hasActionPermission(
                ACTION_PERMISSION_KEYS.firmVirtualBusinessCard,
                ACTION_PERMISSION_VALUES.create,
              ) && (
                <Button
                  onClick={() => {
                    setIsAddingBusinessCard(true);
                  }}
                  variant="outline"
                  size="sm"
                >
                  <PlusCircle className="h-4 w-4" />
                  <Typography className="hidden md:ml-2 md:block">
                    Add business card
                  </Typography>
                </Button>
              )}
            </>
          }
        />
      )}
      <AddOrEditFirmBusinessCardModal
        open={isAddingBusinessCard}
        setOpen={(open: boolean) => {
          if (!open) {
            setIsAddingBusinessCard(false);
            setEditingBusinessCard(undefined);
          }
        }}
        businessCard={editingBusinessCard}
      />
      {editingBusinessCard && (
        <AddOrEditFirmBusinessCardModal
          open={!!editingBusinessCard}
          setOpen={(open: boolean) => {
            if (!open) {
              setEditingBusinessCard(undefined);
            }
          }}
          businessCard={editingBusinessCard}
        />
      )}
    </div>
  );
}

export function FirmBusinessCardsView() {
  return (
    <ErrorBoundary
      fallback={
        <Typography variant="subHeading">{serverErrorFallbackText}</Typography>
      }
    >
      <FirmBusinessCardsViewDangerous />
    </ErrorBoundary>
  );
}
